import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import SmoothScrollProvider, { useLenis } from "@/components/SmoothScrollProvider";
import ScrollScrubHero from "@/components/hero/ScrollScrubHero";
import { StickyNav } from "@/components/StickyNav";
import { WarehouseSection } from "@/components/WarehouseSection";

// three.js + the 360 player only load when someone actually visits /warehouse
const WarehousePage = lazy(() => import("@/components/warehouse/WarehousePage"));
import { FabricsSection } from "@/components/FabricsSection";
import { AboutSection } from "@/components/AboutSection";
import { PricingSection } from "@/components/PricingSection";
import { FAQSection } from "@/components/FAQSection";
import { ContactSection } from "@/components/ContactSection";
import { FabricReel } from "@/components/FabricReel";
import { Footer } from "@/components/Footer";
// Cluster page content is heavy (six long intros + FAQs); load it only when a
// cluster route is visited so the home bundle stays lean.
const ClusterPage = lazy(() => import("@/components/clusters/ClusterPage"));
import { CLUSTER_META } from "@/components/clusters/clusterMeta";

// BrowserRouter doesn't reset scroll on navigation; land every route at the top
// (both native scroll and Lenis's internal offset).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenis]);
  return null;
};

const Home = () => {
  return (
    <>
      <StickyNav />
      <main className="bg-ink">
        {/* Single visible-to-crawlers H1 for the home route; the hero headlines
            are h2 so the page has exactly one keyword-rich H1. */}
        <h1 className="sr-only">
          American-Made Spandex and Lycra Fabric, Wholesale by the Roll or Pound
        </h1>
        <ScrollScrubHero />
        <WarehouseSection />
        <FabricsSection />
        <AboutSection />
        <PricingSection />
        <FAQSection />
        <ContactSection />
        <FabricReel />
      </main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <SmoothScrollProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/warehouse"
            element={
              <Suspense
                fallback={
                  <div className="flex min-h-screen items-center justify-center bg-ink">
                    <p className="eyebrow animate-pulse text-[12px] text-warmwhite">
                      Loading the warehouse…
                    </p>
                  </div>
                }
              >
                <WarehousePage />
              </Suspense>
            }
          />
          {CLUSTER_META.map((p) => (
            <Route
              key={p.slug}
              path={`/${p.slug}`}
              element={
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center bg-ink">
                      <p className="eyebrow animate-pulse text-[12px] text-warmwhite">
                        Loading…
                      </p>
                    </div>
                  }
                >
                  <ClusterPage slug={p.slug} />
                </Suspense>
              }
            />
          ))}
        </Routes>
      </BrowserRouter>
    </SmoothScrollProvider>
  );
}

export default App;
